/**
 * Hook for managing portfolio views and view types
 */

import { useState } from 'react';
import { getTierColors } from '../../services/TierColorService';

/**
 * Custom hook for managing portfolio views
 * @returns {Object} View state and utility functions
 */
const usePortfolioViews = () => {
  // State for view management
  const [pinnedViewType, setPinnedViewType] = useState('table'); // 'table' or 'cards'
  const [portfolioType, setPortfolioType] = useState('custom'); // 'custom' or 'manager'
  const [activeManager, setActiveManager] = useState(''); // Currently active manager for portfolio
  const [isHardPinned, setIsHardPinned] = useState(false); // State for hard pin feature
  const [hardPinnedClients, setHardPinnedClients] = useState([]); // Store hard-pinned clients
  const [useUniformColor, setUseUniformColor] = useState(false); // Uniform color toggle

  // Toggle between table and card view
  const toggleViewType = () => {
    setPinnedViewType(pinnedViewType === 'table' ? 'cards' : 'table');
  };

  // Toggle hard pin feature
  const toggleHardPin = () => {
    setIsHardPinned(!isHardPinned);
  };

  // Toggle uniform color for all clients
  const toggleUniformColor = () => {
    setUseUniformColor(!useUniformColor);
  };

  // Pin all clients for a manager
  const pinManagerClients = (manager, clientData, pinnedClients, orderedPinnedClients, setPinnedClients, setOrderedPinnedClients, lockedClients, getClientKey) => {
    if (!manager || manager === 'All') return;
    
    // Find all clients for this manager
    const managerClients = clientData.filter(client => 
      client.conference_manager === manager
    );
    
    // Always preserve locked clients, regardless of hard pin state
    const lockedClientsList = pinnedClients.filter(client => 
      lockedClients && lockedClients.has(getClientKey(client))
    );
    
    // If hard pinned, keep existing pins and add new ones
    // Otherwise, only keep locked clients
    const existingPins = isHardPinned ? [...pinnedClients] : [...lockedClientsList];
    const existingOrderedPins = isHardPinned ? [...orderedPinnedClients] : [...lockedClientsList];
    
    // Set portfolio type to manager and store active manager
    setPortfolioType('manager');
    setActiveManager(manager);
    
    // Pin all clients for this manager
    if (managerClients.length > 0) {
      const newPinnedClients = [...existingPins];
      const newOrderedPinnedClients = [...existingOrderedPins];
      
      managerClients.forEach(client => {
        // Skip if client is already pinned
        if (existingPins.some(pinned => 
          pinned.client_name === client.client_name && pinned.country === client.country
        )) {
          return;
        }
        
        // Get tier-based colors
        const tierColors = getTierColors();
        const clientTier = client['Tier'] || 'Unknown';
        const tierColorData = tierColors[clientTier] || tierColors['Unknown'];
        
        const clientWithColor = {
          ...client,
          cardColor: tierColorData.light,
          cardAccent: tierColorData.dark
        };
        
        newPinnedClients.push(clientWithColor);
        newOrderedPinnedClients.push(clientWithColor);
      });
      
      setPinnedClients(newPinnedClients);
      setOrderedPinnedClients(newOrderedPinnedClients);
      
      // Store the clients for reference, but don't automatically enable hard pin
      if (existingPins.length === 0) {
        setHardPinnedClients(newPinnedClients);
      }
    } else {
      // If no manager clients found, still preserve locked clients
      setPinnedClients(lockedClientsList);
      setOrderedPinnedClients(lockedClientsList);
    }
  };

  return {
    // State
    pinnedViewType,
    portfolioType,
    activeManager,
    isHardPinned,
    hardPinnedClients,
    useUniformColor,
    
    // Methods
    toggleViewType,
    toggleHardPin,
    toggleUniformColor,
    pinManagerClients,
    
    // Internal utilities
    setPinnedViewType,
    setPortfolioType,
    setActiveManager,
    setIsHardPinned,
    setHardPinnedClients,
    setUseUniformColor
  };
};

export default usePortfolioViews;
